import { StorageKeys } from '../constants/storageKeys';
import { HubConnection, HubConnectionBuilder, HubConnectionState, LogLevel, HttpTransportType } from '@microsoft/signalr';
import { logger } from '../utils/logger';

const RETRO_HUB_URL = process.env.REACT_APP_RETRO_HUB_URL || 'http://localhost:5001/retroHub';
const POKER_HUB_URL = process.env.REACT_APP_POKER_HUB_URL || 'http://localhost:5001/pokerHub';

const SIGNALR_LOG_LEVEL = process.env.NODE_ENV === 'production' ? LogLevel.Error : LogLevel.Information;

// Lazily evaluated so module-level code doesn't crash in SSR / non-browser environments.
const isSafari = typeof navigator !== 'undefined' && /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

// Type for handler callback functions
type HandlerCallback = (...args: any[]) => void;

export class SignalRService {
  private retroConnection: HubConnection | null = null;
  private pokerConnection: HubConnection | null = null;

  // Single-layer handler storage: Map<eventName, callback>
  // Browser tabs each run in their own JS context so one handler per event is sufficient
  private retroHandlers = new Map<string, HandlerCallback>();
  private pokerHandlers = new Map<string, HandlerCallback>();

  private retroRegisteredEvents = new Set<string>();
  private pokerRegisteredEvents = new Set<string>();

  private connectedListeners: Array<() => void> = [];
  private reconnectingListeners: Array<() => void> = [];
  private reconnectedListeners: Array<() => void> = [];
  private closedListeners: Array<() => void> = [];

  private currentRetroId: string | null = null;
  private currentRetroParticipantId: string | null = null;
  private currentPokerSessionId: string | null = null;
  private currentPokerParticipantId: string | null = null;

  // Track which URL and user the connection was built with to detect auth state changes
  private retroConnectionUrl: string | null = null;
  private pokerConnectionUrl: string | null = null;
  // For auth connections the URL is always the same, so also track userId
  private retroConnectionUserId: string | null = null;
  private pokerConnectionUserId: string | null = null;

  // Retry counters for onclose-triggered token refresh (prevent infinite loops)
  private retroCloseRetryCount = 0;
  private pokerCloseRetryCount = 0;

  // Shared token refresh — deduplicates parallel refresh calls from retro+poker onclose
  private tokenRefreshPromise: Promise<void> | null = null;

  // Mutex promises — prevent concurrent start() calls from creating duplicate connections
  private retroStartPromise: Promise<void> | null = null;
  private pokerStartPromise: Promise<void> | null = null;

  // Start a specific hub, or both. Callers on a retro/poker page pass their own type so a failure
  // to connect the OTHER hub can never reject this call and suppress this page's handler
  // registration. The no-arg form (eager warm-up on login) uses allSettled for the same reason.
  async start(type?: 'retro' | 'poker'): Promise<void> {
    if (type === 'retro') { await this.startRetroConnection(); return; }
    if (type === 'poker') { await this.startPokerConnection(); return; }
    await Promise.allSettled([
      this.startRetroConnection(),
      this.startPokerConnection()
    ]);
  }

  private startRetroConnection(): Promise<void> {
    if (this.retroStartPromise) {
      logger.log('🔵 [SignalR Retro] Connection already starting, waiting...');
      return this.retroStartPromise;
    }
    this.retroStartPromise = this._doStartConnection('retro').finally(() => {
      this.retroStartPromise = null;
    });
    return this.retroStartPromise;
  }

  private startPokerConnection(): Promise<void> {
    if (this.pokerStartPromise) {
      logger.log('🔵 [SignalR Poker] Connection already starting, waiting...');
      return this.pokerStartPromise;
    }
    this.pokerStartPromise = this._doStartConnection('poker').finally(() => {
      this.pokerStartPromise = null;
    });
    return this.pokerStartPromise;
  }

  // Returns a per-type accessor so the shared _doStartConnection method can read/write
  // the correct class fields without duplicating logic for retro vs poker.
  private ctx(type: 'retro' | 'poker') {
    const isRetro = type === 'retro';
    const s = this;
    return {
      HUB_URL: isRetro ? RETRO_HUB_URL : POKER_HUB_URL,
      LABEL: isRetro ? 'Retro' : 'Poker',
      JOIN_METHOD: isRetro ? 'JoinRetroBoard' : 'JoinPokerSession',
      get connection() { return isRetro ? s.retroConnection : s.pokerConnection; },
      set connection(c: HubConnection | null) { if (isRetro) s.retroConnection = c; else s.pokerConnection = c; },
      get connectionUrl() { return isRetro ? s.retroConnectionUrl : s.pokerConnectionUrl; },
      set connectionUrl(u: string | null) { if (isRetro) s.retroConnectionUrl = u; else s.pokerConnectionUrl = u; },
      get connectionUserId() { return isRetro ? s.retroConnectionUserId : s.pokerConnectionUserId; },
      set connectionUserId(id: string | null) { if (isRetro) s.retroConnectionUserId = id; else s.pokerConnectionUserId = id; },
      get handlers() { return isRetro ? s.retroHandlers : s.pokerHandlers; },
      get registeredEvents() { return isRetro ? s.retroRegisteredEvents : s.pokerRegisteredEvents; },
      get closeRetryCount() { return isRetro ? s.retroCloseRetryCount : s.pokerCloseRetryCount; },
      set closeRetryCount(n: number) { if (isRetro) s.retroCloseRetryCount = n; else s.pokerCloseRetryCount = n; },
      get currentId() { return isRetro ? s.currentRetroId : s.currentPokerSessionId; },
      get currentParticipantId() { return isRetro ? s.currentRetroParticipantId : s.currentPokerParticipantId; },
      reRegisterHandlers() { if (isRetro) s.reRegisterRetroHandlers(); else s.reRegisterPokerHandlers(); },
      startConnection() { return isRetro ? s.startRetroConnection() : s.startPokerConnection(); },
    };
  }

  private async _doStartConnection(type: 'retro' | 'poker'): Promise<void> {
    const c = this.ctx(type);
    let guest: { token?: string } = {};
    try { guest = JSON.parse(sessionStorage.getItem(StorageKeys.GUEST) || '{}'); }
    catch { sessionStorage.removeItem(StorageKeys.GUEST); }
    const guestToken = guest?.token;
    const hubUrl = guestToken ? `${c.HUB_URL}?access_token=${guestToken}` : c.HUB_URL;
    let currentUserId: string | null = null;
    if (!guestToken) {
      try { currentUserId = JSON.parse(localStorage.getItem(StorageKeys.USER) || 'null')?.id ?? null; }
      catch { localStorage.removeItem(StorageKeys.USER); }
    }

    if (c.connection?.state === HubConnectionState.Connected ||
        c.connection?.state === HubConnectionState.Connecting) {
      if (c.connectionUrl === hubUrl && c.connectionUserId === currentUserId) {
        logger.log(`✅ [SignalR ${c.LABEL}] Already connected/connecting with correct auth`);
        return;
      }
      logger.log(`🔄 [SignalR ${c.LABEL}] Auth state changed, restarting connection`, {
        was: c.connectionUrl?.includes('access_token') ? 'guest' : `auth:${c.connectionUserId}`,
        now: hubUrl.includes('access_token') ? 'guest' : `auth:${currentUserId}`
      });
      await c.connection.stop();
      c.connection = null;
      // Keep the handler callbacks so we can re-attach them to the fresh socket below (previously
      // handlers were cleared too, so after a token-refresh rebuild the new socket had no handlers
      // and was never rejoined to the server group → guests silently lost all realtime updates).
    }

    // A brand-new HubConnection is about to be built, so any names in registeredEvents refer to
    // .on() bindings on the previous, discarded connection object. Clear unconditionally — not
    // only on the Connected/Connecting restart path above — otherwise a connection that ended up
    // Disconnected (finite retry policy, manual stop) would leave stale entries, needsReHome
    // would read false, and handlers would silently never re-bind to the new socket.
    c.registeredEvents.clear();

    c.connectionUserId = currentUserId;
    c.connectionUrl = hubUrl;
    logger.log(`🔵 [SignalR ${c.LABEL}] Creating connection to:`, c.HUB_URL, isSafari ? '(Safari mode)' : '(Standard mode)');

    const newConnection = new HubConnectionBuilder()
      .withUrl(hubUrl, {
        skipNegotiation: false,
        withCredentials: true,
        transport: isSafari
          ? HttpTransportType.ServerSentEvents | HttpTransportType.LongPolling
          : HttpTransportType.WebSockets | HttpTransportType.ServerSentEvents | HttpTransportType.LongPolling
      })
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: (retryContext) => {
          if (retryContext.previousRetryCount === 0) return 0;
          if (retryContext.previousRetryCount === 1) return 2000;
          if (retryContext.previousRetryCount === 2) return 10000;
          if (retryContext.previousRetryCount === 3) return 30000;
          return 60000;
        }
      })
      .withServerTimeout(180000)
      .withKeepAliveInterval(30000)
      .configureLogging(SIGNALR_LOG_LEVEL)
      .build();

    c.connection = newConnection;

    newConnection.onreconnecting(() => {
      this.reconnectingListeners.forEach(fn => fn());
    });

    newConnection.onreconnected(async () => {
      logger.log(`🔄 [SignalR ${c.LABEL}] Reconnected - re-registering handlers and rejoining group`);
      c.closeRetryCount = 0;
      this.reconnectedListeners.forEach(fn => fn());
      c.reRegisterHandlers();
      // Rejoin group after reconnect — group membership is lost on disconnect
      if (c.currentId && c.currentParticipantId && c.connection) {
        try {
          await c.connection.invoke(c.JOIN_METHOD, c.currentId, c.currentParticipantId);
          logger.log(`✅ [SignalR ${c.LABEL}] Rejoined group after reconnect:`, c.currentId);
        } catch (error) {
          logger.error(`❌ [SignalR ${c.LABEL}] Failed to rejoin group after reconnect:`, error);
        }
      }
    });

    newConnection.onclose(async (error) => {
      this.closedListeners.forEach(fn => fn());
      logger.log(`🔴 [SignalR ${c.LABEL}] Connection closed:`, error?.message);
      const isAuthError = error?.message?.includes('401') || error?.message?.includes('Unauthorized');
      const isGuest = !!sessionStorage.getItem(StorageKeys.GUEST);
      if (isAuthError && !isGuest) {
        if (c.closeRetryCount >= 3) {
          logger.error(`❌ [SignalR ${c.LABEL}] Max retry limit (3) reached, redirecting to login`);
          window.location.href = '/login';
          return;
        }
        c.closeRetryCount++;
        logger.log(`🔄 [SignalR ${c.LABEL}] Auth failure (attempt ${c.closeRetryCount}/3) - attempting token refresh and reconnect`);
        try {
          await this.refreshAuthToken();
          logger.log(`✅ [SignalR ${c.LABEL}] Token refreshed, restarting connection`);
          c.connection = null;
          c.connectionUrl = null;
          c.registeredEvents.clear();
          await c.startConnection();
          c.reRegisterHandlers();
          c.closeRetryCount = 0;
          if (c.currentId && c.currentParticipantId && c.connection) {
            await (c.connection as HubConnection).invoke(c.JOIN_METHOD, c.currentId, c.currentParticipantId);
            logger.log(`✅ [SignalR ${c.LABEL}] Rejoined group after token refresh`);
          }
        } catch (refreshError) {
          logger.error(`❌ [SignalR ${c.LABEL}] Token refresh failed, redirecting to login`);
          window.location.href = '/login';
        }
      }
    });

    const maxAttempts = 3;
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        await newConnection.start();
        this.connectedListeners.forEach(fn => fn());
        logger.log(`✅ [SignalR ${c.LABEL}] Connected successfully.`);
        // Re-home the fresh socket whenever there are kept handlers that aren't bound to it yet —
        // this covers a token-change rebuild AND the case where an earlier rebuild attempt failed
        // and this later start() succeeded (deriving from state, not just the in-call isRebuild flag,
        // so realtime is never silently lost on the failure path). No-op on first connect (no
        // handlers yet, currentId null; the hook registers + joins afterwards). Both are idempotent:
        // reRegisterHandlers skips already-bound events, and rejoining a group is idempotent server-side.
        const needsReHome = c.handlers.size > 0 && c.registeredEvents.size === 0;
        if (needsReHome) {
          c.reRegisterHandlers();
          if (c.currentId && c.currentParticipantId) {
            try {
              await newConnection.invoke(c.JOIN_METHOD, c.currentId, c.currentParticipantId);
              logger.log(`✅ [SignalR ${c.LABEL}] Rejoined group after rebuild`);
            } catch (rejoinError) {
              logger.error(`❌ [SignalR ${c.LABEL}] Failed to rejoin group after rebuild:`, rejoinError);
            }
          }
        }
        return;
      } catch (error) {
        const msg = (error as Error)?.message || '';
        const isAuthError = msg.includes('401') || msg.includes('Unauthorized');
        const isGuest = !!sessionStorage.getItem(StorageKeys.GUEST);
        logger.error(`❌ [SignalR ${c.LABEL}] Connection error (attempt ${attempt}/${maxAttempts}):`, error);

        // Negotiate can 401 on an expired access cookie before the REST-side axios interceptor
        // gets a chance to refresh it — refresh here too so we don't burn all retries against a
        // stale cookie (onclose below only covers connections that were already established).
        if (isAuthError && !isGuest) {
          try {
            await this.refreshAuthToken();
            logger.log(`✅ [SignalR ${c.LABEL}] Token refreshed after negotiate 401, retrying`);
            continue;
          } catch (refreshError) {
            logger.error(`❌ [SignalR ${c.LABEL}] Token refresh failed during initial connect:`, refreshError);
          }
        }

        if (attempt < maxAttempts) {
          // Wait before retry — lets proxy routing stabilise
          await new Promise(resolve => setTimeout(resolve, 1500 * attempt));
        } else {
          throw error;
        }
      }
    }
  }

  private async ensureConnectionReady(connection: HubConnection | null, label: string, maxWaitMs: number = 10000): Promise<boolean> {
    if (!connection) {
      logger.error(`❌ [SignalR] ${label} connection is null`);
      return false;
    }

    if (connection.state === HubConnectionState.Connected) {
      return true;
    }

    if (connection.state === HubConnectionState.Connecting) {
      logger.log(`⏳ [SignalR] Waiting for ${label} connection...`);
      const startTime = Date.now();

      while (connection.state === HubConnectionState.Connecting && Date.now() - startTime < maxWaitMs) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      return (connection.state as HubConnectionState) === HubConnectionState.Connected;
    }

    logger.error(`❌ [SignalR] ${label} connection not ready. State:`, connection.state);
    return false;
  }

  private ensureRetroConnectionReady(maxWaitMs: number = 10000): Promise<boolean> {
    return this.ensureConnectionReady(this.retroConnection, 'Retro', maxWaitMs);
  }

  private ensurePokerConnectionReady(maxWaitMs: number = 10000): Promise<boolean> {
    return this.ensureConnectionReady(this.pokerConnection, 'Poker', maxWaitMs);
  }

  private reRegisterRetroHandlers(): void {
    if (!this.retroConnection) return;

    // withAutomaticReconnect reuses the same HubConnection object, so handlers registered via .on()
    // survive the reconnect. We only need to re-add handlers that somehow lost registration.
    // Calling off()+on() would create a brief event-drop window — skip events already registered.
    this.retroHandlers.forEach((_callback, eventName) => {
      if (this.retroRegisteredEvents.has(eventName)) {
        logger.log(`✅ [SignalR] Handler for ${eventName} still active after reconnect`);
        return;
      }
      this.retroRegisteredEvents.add(eventName);
      this.retroConnection!.on(eventName, (...args: any[]) => {
        logger.log(`🔵 [SignalR Event] ${eventName} received`);
        const currentCallback = this.retroHandlers.get(eventName);
        if (currentCallback) {
          try {
            currentCallback(...args);
          } catch (error) {
            logger.error(`❌ [SignalR] Error in handler for ${eventName}:`, error);
          }
        }
      });
      logger.log(`✅ [SignalR] Re-registered missing handler for ${eventName}`);
    });
  }

  private reRegisterPokerHandlers(): void {
    if (!this.pokerConnection) return;

    this.pokerHandlers.forEach((_callback, eventName) => {
      if (this.pokerRegisteredEvents.has(eventName)) {
        logger.log(`✅ [SignalR] Poker handler for ${eventName} still active after reconnect`);
        return;
      }
      this.pokerRegisteredEvents.add(eventName);
      this.pokerConnection!.on(eventName, (...args: any[]) => {
        logger.log(`🔵 [SignalR Event] ${eventName} received`);
        const currentCallback = this.pokerHandlers.get(eventName);
        if (currentCallback) {
          try {
            currentCallback(...args);
          } catch (error) {
            logger.error(`❌ [SignalR] Error in handler for ${eventName}:`, error);
          }
        }
      });
      logger.log(`✅ [SignalR] Re-registered missing poker handler for ${eventName}`);
    });
  }

  private registerRetroHandler(eventName: string, callback: HandlerCallback): () => void {
    if (!this.retroConnection) {
      logger.error('❌ [SignalR] Cannot register handler - connection is null!');
      return () => {};
    }

    // Update callback - the master handler dispatches to whatever is current in retroHandlers
    this.retroHandlers.set(eventName, callback);

    // Register master handler on the connection only once per event
    if (!this.retroRegisteredEvents.has(eventName)) {
      this.retroRegisteredEvents.add(eventName);
      this.retroConnection.on(eventName, (...args: any[]) => {
        logger.log(`🔵 [SignalR Event] ${eventName} received`);
        const currentCallback = this.retroHandlers.get(eventName);
        if (currentCallback) {
          try {
            currentCallback(...args);
          } catch (error) {
            logger.error(`❌ [SignalR] Error in handler for ${eventName}:`, error);
          }
        }
      });
      logger.log(`✅ [SignalR] Registered handler for ${eventName}`);
    }

    return () => {
      // Only remove the callback from the map — do NOT call off() or remove from registeredEvents.
      // Keeping the master handler registered prevents a brief event-drop window when handlers
      // are torn down and re-registered (e.g., component remount). The master handler no-ops
      // when retroHandlers has no entry for this event.
      this.retroHandlers.delete(eventName);
      logger.log(`🧹 [SignalR] Removed handler for ${eventName}`);
    };
  }

  private registerPokerHandler(eventName: string, callback: HandlerCallback): () => void {
    if (!this.pokerConnection) {
      logger.error('❌ [SignalR] Cannot register handler - connection is null!');
      return () => {};
    }

    this.pokerHandlers.set(eventName, callback);

    if (!this.pokerRegisteredEvents.has(eventName)) {
      this.pokerRegisteredEvents.add(eventName);
      this.pokerConnection.on(eventName, (...args: any[]) => {
        logger.log(`🔵 [SignalR Event] ${eventName} received`);
        const currentCallback = this.pokerHandlers.get(eventName);
        if (currentCallback) {
          try {
            currentCallback(...args);
          } catch (error) {
            logger.error(`❌ [SignalR] Error in handler for ${eventName}:`, error);
          }
        }
      });
      logger.log(`✅ [SignalR] Registered poker handler for ${eventName}`);
    }

    return () => {
      // Same as retro: keep master handler alive, only remove the callback.
      this.pokerHandlers.delete(eventName);
      logger.log(`🧹 [SignalR] Removed poker handler for ${eventName}`);
    };
  }

  getRetroConnection(): HubConnection | null {
    return this.retroConnection;
  }

  getPokerConnection(): HubConnection | null {
    return this.pokerConnection;
  }

  // Shared token refresh — if retro and poker onclose fire in parallel, only one HTTP call is made
  private refreshAuthToken(): Promise<void> {
    if (this.tokenRefreshPromise) return this.tokenRefreshPromise;
    const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';
    this.tokenRefreshPromise = fetch(`${API_BASE_URL}/api/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: '{}'
    }).then(async (res) => {
      if (!res.ok) throw new Error(`Token refresh failed: ${res.status}`);
    }).finally(() => { this.tokenRefreshPromise = null; });
    return this.tokenRefreshPromise;
  }

  getTabId(): string {
    return 'single-tab';
  }

  onConnectionConnected(callback: () => void): void {
    this.connectedListeners.push(callback);
  }

  offConnectionConnected(callback: () => void): void {
    this.connectedListeners = this.connectedListeners.filter(fn => fn !== callback);
  }

  onConnectionReconnecting(callback: () => void): void {
    this.reconnectingListeners.push(callback);
  }

  offConnectionReconnecting(callback: () => void): void {
    this.reconnectingListeners = this.reconnectingListeners.filter(fn => fn !== callback);
  }

  onConnectionReconnected(callback: () => void): void {
    this.reconnectedListeners.push(callback);
  }

  offConnectionReconnected(callback: () => void): void {
    this.reconnectedListeners = this.reconnectedListeners.filter(fn => fn !== callback);
  }

  onConnectionClosed(callback: () => void): void {
    this.closedListeners.push(callback);
  }

  offConnectionClosed(callback: () => void): void {
    this.closedListeners = this.closedListeners.filter(fn => fn !== callback);
  }

  async stop(): Promise<void> {
    const stopPromises = [];

    if (this.retroConnection) {
      stopPromises.push(this.retroConnection.stop().then(() => {
        logger.log('🔴 [SignalR Retro] Connection stopped');
        this.retroConnection = null;
        this.retroHandlers.clear();
        this.retroRegisteredEvents.clear();
        this.currentRetroId = null;
        this.currentRetroParticipantId = null;
        this.retroConnectionUrl = null;
        this.retroConnectionUserId = null;
        this.retroCloseRetryCount = 0;
      }));
    }

    if (this.pokerConnection) {
      stopPromises.push(this.pokerConnection.stop().then(() => {
        logger.log('🔴 [SignalR Poker] Connection stopped');
        this.pokerConnection = null;
        this.pokerHandlers.clear();
        this.pokerRegisteredEvents.clear();
        this.currentPokerSessionId = null;
        this.currentPokerParticipantId = null;
        this.pokerConnectionUrl = null;
        this.pokerConnectionUserId = null;
        this.pokerCloseRetryCount = 0;
      }));
    }

    await Promise.all(stopPromises);
  }

  async joinRetroBoard(retroId: string, participantId: string): Promise<void> {
    const isReady = await this.ensureRetroConnectionReady();
    if (!isReady) {
      throw new Error('SignalR connection not ready');
    }

    logger.log('🔵 [SignalR] Joining retro board:', retroId, 'Participant:', participantId, 'Tab:', '[tab]');
    await this.retroConnection!.invoke('JoinRetroBoard', retroId, participantId);
    this.currentRetroId = retroId;
    this.currentRetroParticipantId = participantId;
    logger.log('✅ [SignalR] Joined retro board successfully');
  }

  async leaveRetroBoard(retroId: string): Promise<void> {
    if (this.retroConnection && this.retroConnection.state === HubConnectionState.Connected) {
      await this.retroConnection.invoke('LeaveRetroBoard', retroId);
    }
    // Clear stored group so we don't rejoin after reconnect (intentional leave)
    this.currentRetroId = null;
    this.currentRetroParticipantId = null;
  }

  onCardAdded(callback: (card: any) => void): () => void {
    return this.registerRetroHandler('retro:cardadded', callback);
  }

  onCardUpdated(callback: (card: any) => void): () => void {
    return this.registerRetroHandler('retro:cardupdated', callback);
  }

  onCardDeleted(callback: (card: any) => void): () => void {
    return this.registerRetroHandler('retro:carddeleted', callback);
  }

  onParticipantAdded(callback: (participant: any) => void): () => void {
    return this.registerRetroHandler('retro:participantadded', callback);
  }

  onParticipantLeft(callback: (data: { participantId: string }) => void): () => void {
    return this.registerRetroHandler('retro:participantleft', callback);
  }

  onParticipantOnline(callback: (data: { participantId: string }) => void): () => void {
    return this.registerRetroHandler('retro:participantonline', callback);
  }

  onParticipantOffline(callback: (data: { participantId: string }) => void): () => void {
    return this.registerRetroHandler('retro:participantoffline', callback);
  }

  onCardRevealed(callback: (data: { cardId: string }) => void): () => void {
    return this.registerRetroHandler('retro:cardrevealed', callback);
  }

  onTimerSet(callback: (data: { durationMinutes: number; startedAt: string }) => void): () => void {
    return this.registerRetroHandler('retro:timerset', callback);
  }

  onAllCardsRevealed(callback: () => void): () => void {
    return this.registerRetroHandler('retro:allcardsrevealed', callback);
  }

  onTimerCleared(callback: () => void): () => void {
    return this.registerRetroHandler('retro:timercleared', callback);
  }

  onRetrospectiveClosed(callback: (data: { retrospectiveId: string }) => void): () => void {
    return this.registerRetroHandler('retro:retrospectiveclosed', callback);
  }

  onCardReactionAdded(callback: (data: { cardId: string; reactionId: string; userId: string; emoji: string; createdAt: string }) => void): () => void {
    return this.registerRetroHandler('retro:cardreactionadded', callback);
  }

  onCardReactionRemoved(callback: (data: { cardId: string; reactionId: string }) => void): () => void {
    return this.registerRetroHandler('retro:cardreactionremoved', callback);
  }

  onCardCommentAdded(callback: (data: { cardId: string; commentId: string; userId: string; userDisplayName: string; content: string; createdAt: string; isEdited: boolean; lastEditedAt?: string; imageUrl?: string; gifUrl?: string }) => void): () => void {
    return this.registerRetroHandler('retro:cardcommentadded', callback);
  }

  onCardCommentUpdated(callback: (data: { cardId: string; commentId: string; content: string; isEdited: boolean; lastEditedAt?: string }) => void): () => void {
    return this.registerRetroHandler('retro:cardcommentupdated', callback);
  }

  onCardCommentDeleted(callback: (data: { cardId: string; commentId: string }) => void): () => void {
    return this.registerRetroHandler('retro:cardcommentdeleted', callback);
  }

  async joinPokerSession(sessionId: string, participantId: string): Promise<void> {
    const isReady = await this.ensurePokerConnectionReady();
    if (!isReady) {
      throw new Error('SignalR connection not ready');
    }

    logger.log('🃏 [SignalR Poker] Joining poker session:', sessionId, 'Participant:', participantId, 'Tab:', '[tab]');
    await this.pokerConnection!.invoke('JoinPokerSession', sessionId, participantId);
    this.currentPokerSessionId = sessionId;
    this.currentPokerParticipantId = participantId;
    logger.log('✅ [SignalR Poker] Joined poker session successfully');
  }

  async leavePokerSession(sessionId: string, participantId: string): Promise<void> {
    if (this.pokerConnection && this.pokerConnection.state === HubConnectionState.Connected) {
      logger.log('🃏 [SignalR Poker] Leaving poker session:', sessionId, 'Participant:', participantId, 'Tab:', '[tab]');
      await this.pokerConnection.invoke('LeavePokerSession', sessionId, participantId);
      logger.log('✅ [SignalR Poker] Left poker session successfully');
    }
    // Clear stored group so we don't rejoin after reconnect (intentional leave)
    this.currentPokerSessionId = null;
    this.currentPokerParticipantId = null;
  }

  onPokerParticipantJoined(callback: (data: { participantId: string; userId?: string; displayName: string; joinedAt: string }) => void): () => void {
    return this.registerPokerHandler('poker:participantjoined', callback);
  }

  onPokerParticipantLeft(callback: (data: { participantId: string }) => void): () => void {
    return this.registerPokerHandler('poker:participantleft', callback);
  }

  onPokerRoundStarted(callback: (data: { roundId: string; taskCode?: string; taskName: string; timerDurationSeconds: number; startedAt: string }) => void): () => void {
    return this.registerPokerHandler('poker:roundstarted', callback);
  }

  onPokerVoteSubmitted(callback: (data: { roundId: string; participantId: string; hasVoted: boolean }) => void): () => void {
    return this.registerPokerHandler('poker:votesubmitted', callback);
  }

  onPokerVotesRevealed(callback: (data: { roundId: string; taskName: string; taskCode?: string; votes: Array<{ participantId: string; participantDisplayName: string; score: number }>; consensusScore: number }) => void): () => void {
    return this.registerPokerHandler('poker:votesrevealed', callback);
  }

  onPokerSessionCompleted(callback: (data: { sessionId: string }) => void): () => void {
    return this.registerPokerHandler('poker:sessioncompleted', callback);
  }

  onPokerFinalScoreSet(callback: (data: { roundId: string; finalScore: number }) => void): () => void {
    return this.registerPokerHandler('poker:finalscoreset', callback);
  }

  onPokerParticipantIdleChanged(callback: (data: { participantId: string; isIdle: boolean }) => void): () => void {
    return this.registerPokerHandler('poker:participantidlechanged', callback);
  }
}

export const signalRService = new SignalRService();
